import { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import {
    ElectronAPI,
    AppAPI,
    CryptoAPI,
    HostAPI,
    NetworkAPI,
    ScreenCaptureAPI,
    ScreenshotAPI,
    StorageAPI,
    UpdatesAPI,
    UtilsAPI,
    WindowAPI
} from '../types/electron';
import { satisfiesVersion, VersionConstraint } from '../helpers/semver';
import { normalizeElectronApi, type LegacyExtras } from '../helpers/electron-adapter';

/**
 * Valor del contexto de Electron
 * Expone todas las APIs de Electron de forma desestructurable
 */
export interface ElectronContextValue {
    /** true si la app está corriendo en Electron */
    isElectron: boolean;
    /** true si la API de Electron es la versión legacy (desktop-app antiguo) */
    isLegacy: boolean;
    /** Versión de la app Electron (null si no disponible o cargando) */
    version: string | null;
    /** true mientras se está obteniendo la versión */
    versionLoading: boolean;

    // APIs de Electron (null si no está en Electron)
    /** Window management */
    window: WindowAPI | null;
    /** App information and control */
    app: AppAPI | null;
    /** Auto-updates system */
    updates: UpdatesAPI | null;
    /** Screen sharing and capture */
    screenCapture: ScreenCaptureAPI | null;
    /** Utility functions */
    utils: UtilsAPI | null;
    /** Encrypted storage operations */
    storage: StorageAPI | null;
    /** Network mode management */
    network: NetworkAPI | null;
    /** Host system interaction */
    host: HostAPI | null;
    /** Cryptographic operations (bcrypt) */
    crypto: CryptoAPI | null;
    /** Screenshot capture and management */
    screenshot: ScreenshotAPI | null;

    /**
     * API completa de Electron (para compatibilidad hacia atrás)
     * @deprecated Usar las APIs individuales directamente
     */
    electron: ElectronAPI | null;

    // Propiedades extra de la API legacy (null si no es legacy o no es Electron)
    /** Token de autenticación de Comitas (solo disponible en desktop-app legacy) */
    comitasToken: string | null;
    /** Plataforma del sistema operativo (solo disponible en desktop-app legacy) */
    platform: string | null;
}

const defaultContextValue: ElectronContextValue = {
    isElectron: false,
    isLegacy: false,
    version: null,
    versionLoading: false,
    window: null,
    app: null,
    updates: null,
    screenCapture: null,
    utils: null,
    storage: null,
    network: null,
    host: null,
    crypto: null,
    screenshot: null,
    electron: null,
    comitasToken: null,
    platform: null,
};

const ElectronContext = createContext<ElectronContextValue>(defaultContextValue);

interface ElectronProviderProps {
    children: ReactNode;
}

/**
 * Detecta si estamos en un entorno Electron
 * Se calcula una sola vez al cargar el módulo
 */
const detectElectron = (): boolean => {
    return typeof window !== 'undefined' && window.electron !== undefined;
};

/**
 * Provider para el contexto de Electron
 * Proporciona acceso a la API de Electron y un flag isElectron
 * para renderizado condicional
 */
export function ElectronProvider({ children }: ElectronProviderProps) {
    // Estado para la versión (se obtiene de forma async)
    const [version, setVersion] = useState<string | null>(null);
    const [versionLoading, setVersionLoading] = useState<boolean>(false);

    // Detectar Electron una sola vez
    const isElectron = useMemo(() => detectElectron(), []);

    // Normalizar la API: adaptar legacy (plana) a modular si es necesario
    const { electron, isLegacy, extras } = useMemo(() => {
        if (!isElectron || !window.electron) {
            return { electron: null, isLegacy: false, extras: null as LegacyExtras | null };
        }
        return normalizeElectronApi(window.electron);
    }, [isElectron]);

    // Obtener la versión al montar si estamos en Electron
    useEffect(() => {
        if (!electron) return;

        setVersionLoading(true);
        electron.app.getVersion()
            .then((ver) => {
                setVersion(ver);
            })
            .catch((err) => {
                console.error('[ElectronContext] Error getting app version:', err);
                setVersion(null);
            })
            .finally(() => {
                setVersionLoading(false);
            });
    }, [electron]);

    // Memoizamos el valor para evitar re-renders innecesarios
    // Exponemos cada API individualmente para permitir desestructuración
    const value = useMemo<ElectronContextValue>(() => ({
        // Flags y metadatos
        isElectron,
        isLegacy,
        version,
        versionLoading,

        // APIs individuales (null si no está en Electron)
        window: electron?.window ?? null,
        app: electron?.app ?? null,
        updates: electron?.updates ?? null,
        screenCapture: electron?.screenCapture ?? null,
        utils: electron?.utils ?? null,
        storage: electron?.storage ?? null,
        network: electron?.network ?? null,
        host: electron?.host ?? null,
        crypto: electron?.crypto ?? null,
        screenshot: electron?.screenshot ?? null,

        // API completa (para compatibilidad hacia atrás)
        electron,

        // Propiedades extra de la API legacy
        comitasToken: extras?.comitasToken ?? null,
        platform: extras?.platform ?? null,
    }), [electron, isElectron, isLegacy, extras, version, versionLoading]);

    return (
        <ElectronContext.Provider value={value}>
            {children}
        </ElectronContext.Provider>
    );
}

/**
 * Hook para verificar si estamos en Electron
 * Más eficiente que useIsElectron() ya que usa el contexto memoizado
 * @returns true si está en Electron
 */
export function useIsElectronContext(): boolean {
    const context = useContext(ElectronContext);
    return context?.isElectron ?? false;
}

/**
 * Hook para obtener el contexto completo de Electron
 * Útil cuando necesitas tanto la API como el flag isElectron
 * @returns Contexto completo { electron, isElectron }
 */
export function useElectronFullContext(): ElectronContextValue {
    return useContext(ElectronContext);
}

/**
 * Hook para obtener la versión de Electron
 * @returns { version, loading } - versión actual y estado de carga
 */
export function useElectronVersion(): { version: string | null; loading: boolean } {
    const context = useContext(ElectronContext);
    return {
        version: context.version,
        loading: context.versionLoading,
    };
}

/**
 * Hook para verificar si la versión de Electron cumple con los requisitos
 * @param constraint Restricciones de versión { min?, max?, exact? }
 * @returns true si la versión cumple, false si no o si no está en Electron
 */
export function useElectronSupports(constraint: VersionConstraint): boolean {
    const context = useContext(ElectronContext);

    // Si no estamos en Electron, no cumple
    if (!context.isElectron) {
        return false;
    }

    // Si aún está cargando la versión, asumir que no cumple (safe default)
    if (context.versionLoading || !context.version) {
        return false;
    }

    return satisfiesVersion(context.version, constraint);
}

/**
 * Hook para verificar si estamos usando la API legacy del desktop-app antiguo
 * Útil para mostrar mensajes de "actualiza tu app" o deshabilitar features
 * @returns true si la API es legacy
 */
export function useIsLegacyElectron(): boolean {
    const context = useContext(ElectronContext);
    return context?.isLegacy ?? false;
}
